import * as React from 'react';
import { Icon, Input, Table, useTableHelpers } from '@teamsnap/snap-ui';
import { getColumns } from './columns';
import { MemberRegistration, MemberRegistrationsKeyInsights } from './types';
import { debounce } from 'lodash';
import InsightToggleTile from 'components/financials/insights-toggle-tile';
import { formatPenniesToDollars } from 'core/utils';

const DEBOUNCE_TIME = 300;

type Props = {
  orgId: number;
  registrationData: MemberRegistration[];
  registrationKeyInsights: MemberRegistrationsKeyInsights | null;
};

const filterRegistration = (data: MemberRegistration[], searchTerm: string) =>
  data.filter((item) => {
    const search = searchTerm.toLowerCase();
    return (
      item.registrationFormName.toLowerCase().includes(search) ||
      item.orderId.toLowerCase() === search
    );
  });

export default function MemberRegistrations({
  orgId,
  registrationData,
  registrationKeyInsights,
}: Props) {
  const [searchTerm, setSearchTerm] = React.useState('');

  const parsedData = React.useMemo(() => {
    if (!registrationData) return [];
    if (searchTerm) {
      return filterRegistration(registrationData, searchTerm);
    }
    return registrationData;
  }, [registrationData, searchTerm]);

  const columns = getColumns(Number(orgId));

  const { headerRow, rows } = useTableHelpers({
    columns,
    data: parsedData,
    initialSorting: {
      method: 'date',
      sortKey: 'registrationDate',
      acsending: false,
    },
  });

  const debouncedSearchTerm = React.useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, DEBOUNCE_TIME),
    []
  );

  const registrationKeyInsightsDisplay = React.useMemo(() => {
    const overdueColorClass =
      registrationKeyInsights?.outstandingAmount > 0
        ? 'sui-text-negative-text'
        : '';
    return {
      outstandingTotal: (
        <span className={overdueColorClass}>
          {formatPenniesToDollars(registrationKeyInsights?.outstandingAmount)}
        </span>
      ),
      scheduledTotal: formatPenniesToDollars(
        registrationKeyInsights?.scheduledAmount
      ),
      paidTotal: formatPenniesToDollars(registrationKeyInsights?.paidAmount),
    };
  }, [registrationKeyInsights]);

  const handleSearchTermChange = (e) => {
    const { value } = e.target;
    setSearchTerm(value);
    debouncedSearchTerm(value);
  };

  return (
    <section data-testid="MemberRegistrations">
      {registrationData.length > 0 ? (
        <>
          {registrationKeyInsights && (
            <section className="sui-w-fit sui-bg-neutral-background-weak sui-rounded sui-px-4 sui-py-2 sui-mb-2">
              <div
                data-testid="MemberRegistrations__key-insights"
                className="sui-flex sui-gap-4 sui-mb-2"
              >
                <InsightToggleTile
                  data-testid="MemberRegistrations__outstanding"
                  label="Outstanding"
                  value={registrationKeyInsightsDisplay.outstandingTotal}
                />
                <InsightToggleTile
                  data-testid="MemberRegistrations__scheduled"
                  label="Scheduled"
                  value={registrationKeyInsightsDisplay.scheduledTotal}
                />
                <InsightToggleTile
                  data-testid="MemberRegistrations__paid"
                  label="Paid"
                  value={registrationKeyInsightsDisplay.paidTotal}
                />
              </div>
              <p className="sui-flex sui-items-center sui-gap-[4px]">
                <Icon
                  name="info"
                  size="s"
                  className="sui-text-neutral-icon-medium"
                />
                Stats include amount for other participants if they are
                registered in the same orders. e.g. may include amount due for a
                sibling that's registered in the same order.
              </p>
            </section>
          )}
          <div data-testid="MemberRegistrations__filter" className="sui-mb-2">
            <Input
              data-testid="MemberRegistrations__search"
              name="search"
              leftIcon="search"
              type="text"
              placeholder="Search by registration name or order number"
              size="small"
              className="sui-flex-1 sui-max-w-[500px]"
              value={searchTerm}
              onChange={handleSearchTermChange}
              allowClear
            />
          </div>
          <div
            data-testid="MemberRegistrations__table"
            className="sui-overflow-x-auto sui-mb-2"
          >
            <div style={{ width: 'max-content', minWidth: '100%' }}>
              <Table headerRow={headerRow} rows={rows} />
            </div>
          </div>
        </>
      ) : (
        <div
          data-testid="MemberRegistrations__empty"
          className="sui-w-[250px] sui-flex sui-flex-col sui-items-center"
        >
          <svg width="250" height="200" viewBox="0 0 250 200" fill="none">
            <rect width="250" height="200" fill="white" />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M207 65C210.866 65 214 68.134 214 72C214 75.866 210.866 79 207 79H167C170.866 79 174 82.134 174 86C174 89.866 170.866 93 167 93H189C192.866 93 196 96.134 196 100C196 103.866 192.866 107 189 107H178.826C173.952 107 170 110.134 170 114C170 116.577 172 118.911 176 121C179.866 121 183 124.134 183 128C183 131.866 179.866 135 176 135H93C89.134 135 86 131.866 86 128C86 124.134 89.134 121 93 121H54C50.134 121 47 117.866 47 114C47 110.134 50.134 107 54 107H94C97.866 107 101 103.866 101 100C101 96.134 97.866 93 94 93H69C65.134 93 62 89.866 62 86C62 82.134 65.134 79 69 79H109C105.134 79 102 75.866 102 72C102 68.134 105.134 65 109 65H207ZM207 93C210.866 93 214 96.134 214 100C214 103.866 210.866 107 207 107C203.134 107 200 103.866 200 100C200 96.134 203.134 93 207 93Z"
              fill="#F3F7FF"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M153.672 64.0007L162.974 131.843L163.809 138.65C164.079 140.842 162.519 142.838 160.327 143.107L101.766 150.298C99.5738 150.567 97.578 149.008 97.3088 146.815L88.2931 73.3874C88.1584 72.2911 88.9381 71.2932 90.0344 71.1586C90.0413 71.1578 90.0483 71.1569 90.0552 71.1562L94.9136 70.6112M98.8421 70.1705L103.429 69.656L98.8421 70.1705Z"
              fill="white"
            />
            <path
              d="M154.91 63.8309C154.816 63.1469 154.186 62.6685 153.502 62.7623C152.818 62.856 152.34 63.4865 152.433 64.1705L154.91 63.8309ZM162.974 131.843L164.214 131.691C164.214 131.685 164.213 131.679 164.212 131.674L162.974 131.843ZM163.809 138.65L165.05 138.497L163.809 138.65ZM160.327 143.107L160.479 144.348L160.327 143.107ZM101.766 150.298L101.919 151.538L101.766 150.298ZM97.3088 146.815L98.5495 146.663L97.3088 146.815ZM90.0552 71.1562L90.1945 72.3984L90.0552 71.1562ZM95.0529 71.8534C95.739 71.7764 96.2327 71.1579 96.1558 70.4718C96.0788 69.7858 95.4603 69.292 94.7742 69.369L95.0529 71.8534ZM98.7028 68.9283C98.0167 69.0053 97.5229 69.6238 97.5999 70.3098C97.6768 70.9959 98.2954 71.4897 98.9814 71.4127L98.7028 68.9283ZM103.568 70.8982C104.255 70.8212 104.748 70.2027 104.671 69.5166C104.594 68.8306 103.976 68.3368 103.29 68.4137L103.568 70.8982ZM152.433 64.1705L161.735 132.013L164.212 131.674L154.91 63.8309L152.433 64.1705ZM161.733 131.996L162.569 138.802L165.05 138.497L164.214 131.691L161.733 131.996ZM162.569 138.802C162.754 140.31 161.682 141.682 160.174 141.867L160.479 144.348C163.357 143.995 165.403 141.375 165.05 138.497L162.569 138.802ZM160.174 141.867L101.614 149.057L101.919 151.538L160.479 144.348L160.174 141.867ZM101.614 149.057C100.107 149.242 98.7346 148.17 98.5495 146.663L96.0681 146.967C96.4215 149.845 99.0409 151.892 101.919 151.538L101.614 149.057ZM98.5495 146.663L89.5337 73.2351L87.0524 73.5398L96.0681 146.967L98.5495 146.663ZM89.5337 73.2351C89.4833 72.824 89.7756 72.4498 90.1867 72.3993L89.8821 69.9179C88.1005 70.1367 86.8336 71.7582 87.0524 73.5398L89.5337 73.2351ZM90.1867 72.3993C90.1893 72.399 90.1919 72.3987 90.1945 72.3984L89.9159 69.914C89.9046 69.9152 89.8933 69.9165 89.8821 69.9179L90.1867 72.3993ZM90.1945 72.3984L95.0529 71.8534L94.7742 69.369L89.9159 69.914L90.1945 72.3984ZM98.9814 71.4127L103.568 70.8982L103.29 68.4137L98.7028 68.9283L98.9814 71.4127Z"
              fill="#1F64E7"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M151.14 68.2686L159.56 129.752L160.317 135.921C160.561 137.908 159.167 139.714 157.203 139.955L104.761 146.394C102.798 146.635 101.008 145.22 100.764 143.233L92.6141 76.8562C92.4795 75.7599 93.2591 74.762 94.3555 74.6274L100.843 73.8308"
              fill="#E8F0FE"
            />
            <path
              d="M107.922 54C107.922 52.4812 109.153 51.25 110.672 51.25H156.229C156.958 51.25 157.657 51.5395 158.173 52.0549L171.616 65.4898C172.132 66.0056 172.422 66.7053 172.422 67.4349V130C172.422 131.519 171.191 132.75 169.672 132.75H110.672C109.153 132.75 107.922 131.519 107.922 130V54Z"
              fill="white"
              stroke="#1F64E7"
              strokeWidth="2.5"
            />
            <path
              d="M156.672 52.4023V63.9995C156.672 65.6564 158.015 66.9995 159.672 66.9995H167.605"
              stroke="#1F64E7"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M118 118H144M118 67H144H118ZM118 79H161H118ZM118 92H161H118ZM118 105H161H118Z"
              stroke="#75A4FE"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="sui-heading-sm sui-text-neutral-text-medium">
            No registration records.
          </span>
        </div>
      )}
    </section>
  );
}
